<template>
      <div v-if="videoUrl" class="interactive-video-container">
      <div class="video-responsive">
        <div class="video-time-display">
          <div class="current-time">{{ formatTime(currentTime) }}</div>
          <div class="total-time">{{ videoTotal }}</div>
        </div>
        
        <video ref="videoPlayer"  class="video-js vjs-default-skin"
          controls preload="auto" width="100%" height="100%"
        />
        <slot/>
      <!-- Marker Ekle Dropdown -->
      <div class="marker-dropdown-container">
        <button type="button" class="add-marker-btn" @click.prevent="showMarkerDropdown = !showMarkerDropdown">
          +   {{$t('general.add_question')}}
          <span class="dropdown-arrow">&#9660;</span>
        </button>
        <div v-if="showMarkerDropdown" class="marker-dropdown">
          <div
            v-for="type in markerTypes"
            :key="type.value"
            class="marker-dropdown-item"
            @click="addMarkerWithType(type)"
          >
            {{ $t(`general.${type.label}`) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Timeline -->
    <div v-if="videoTotal !== '00:00'" class="custom-timeline">
      <div class="timeline-bar" @click="onTimelineSeek($event)">
        <div class="timeline-images">
          <div
            v-for="(thumb, idx) in timelineThumbnails"
            :key="'timeline-img-' + idx"
            class="timeline-image-thumb"
            :style="{ width: timelineThumbnailWidths[idx] + '%' }"
          >
            <img :src="thumb" alt="frame" />
            <div class="timeline-image-range">{{ timelineThumbnailRanges[idx] }}</div>
          </div>
        </div>
        <div class="timeline-progress" :style="{ width: progressPercent + '%' }" />
        <!-- Markers -->
        <div
          v-for="(marker, idx) in markers"
          :key="marker.id"
          class="timeline-marker"
          :style="getMarkerStyle(marker)"
          @mousedown.prevent="onMarkerDragStart(idx, $event)"
          @touchstart.prevent="onMarkerDragStart(idx, $event)"
          @click.stop.right="handleEditMarker(idx)"
          :title="marker.text || 'Marker'"
        >
          <svg width="36" height="48" viewBox="0 0 36 48" fill="none">
            <ellipse cx="18" cy="18" rx="18" ry="18" fill="#23ade5"/>
            <path d="M18 48C18 48 36 30.7482 36 18C36 8.05887 27.9411 0 18 0C8.05887 0 0 8.05887 0 18C0 30.7482 18 48 18 48Z" fill="#23ade5"/>
          </svg>
        </div>
      </div>
      <!-- v-if sart: .timeline-thumb'lar absolute oldugu icin kap
           `height: 72px` ile sabit; marker yokken bos yer kapliyordu. -->
      <div v-if="markers.length" class="timeline-thumbnails">
        <div
          v-for="(marker, idx) in markers"
          :key="'thumb-' + marker.id"
          class="timeline-thumb"
          :class="{ 'timeline-thumb--no-image': !timelineThumbnails.length }"
          :style="getThumbStyle(marker)"
        >
          <img v-if="timelineThumbnails.length" :src="getClosestThumbnail(marker.time)" alt="thumb" />
          <div class="timeline-thumb-time">{{marker.time_in_video}}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import videojs from 'video.js';
import 'video.js/dist/video-js.min.css';
import { generateVideoFrames } from '@/utils/interactiveVideoHelper';

export default {
  name: 'YsVideo',
  props: {
    videoUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      player: null,
      draggingMarkerIdx: null,
      isDragging: false,
      videoTotal: "00:00",
      currentTime: 0,
      timelineWidth: 0,
      markerWidth: 24,
      thumbWidth: 60,
      placeholderThumb: '',
      timelineThumbnails: [],
      frameInterval: 15,
      framesReady: false,
      showMarkerDropdown: false,
    };
  },
  computed: {
    markerTypes() {
      return this.$store.getters.markerTypes;
    },
    markers() {
      return this.$store.getters.markers;
    },
    progressPercent() {
      if (!this.player || !this.player.duration()) return 0;
      return (this.player.currentTime() / this.player.duration()) * 100;
    },
    timelineThumbnailRanges() {
      const ranges = [];
      const interval = this.frameInterval;
      const total = this.player && this.player.duration() ? this.player.duration() : 0;
      for (let i = 0; i < this.timelineThumbnails.length; i++) {
        const start = i * interval;
        let end = (i + 1) * interval;
        if (end > total) end = total;
        ranges.push(`${this.timeFormat(start)}–${this.timeFormat(end)}`);
      }
      return ranges;
    },
    timelineThumbnailWidths() {
      const total = this.player && this.player.duration() ? this.player.duration() : 0;
      const interval = this.frameInterval;
      const widths = [];
      for (let i = 0; i < this.timelineThumbnails.length; i++) {
        let start = i * interval;
        let end = (i < this.timelineThumbnails.length - 1) ? (i + 1) * interval : total;
        let width = ((end - start) / total) * 100;
        widths.push(width);
      }
      return widths;
    }
  },
  methods: {
    initializeVideoPlayer() {
      const container = this.$refs.videoPlayer;
      if (!container || !this.videoUrl) {
        return;
      }
      
      // Eğer zaten bir player varsa, önce onu temizle
      if (this.player) {
        this.destroyVideoPlayer();
      }
      
      const playerOptions = {
        sources: [{ src: this.videoUrl, type: 'video/mp4' }],
        controls: true,
        preload: 'auto',
        width: '100%',
        height: '100%'
      };
      this.player = videojs(container, playerOptions);
      this.player.on('loadedmetadata', () => {
        this.videoTotal = this.timeFormat(this.player.duration());
        this.$forceUpdate();
        this.generateTimelineThumbnails();
      });
      this.player.on('timeupdate', () => { 
        this.currentTime = this.player.currentTime();
        this.$forceUpdate(); 
      });
    },
    destroyVideoPlayer() {
      if (this.player && this.player.dispose) {
        this.player.dispose();
        this.player = null;
      }
      this.videoTotal = "00:00";
      this.framesReady = false;
      this.timelineThumbnails = [];
    },
    async generateTimelineThumbnails() {
      if (!this.videoUrl || !this.player) return;
      const duration = this.player.duration();
      if (!duration || isNaN(duration) || duration <= 0) return;
      this.framesReady = false;
      const { thumbnails, interval } = await generateVideoFrames(this.videoUrl, duration, { maxThumbnails: 10, minThumbnails: 6 });
      this.timelineThumbnails = thumbnails;
      this.frameInterval = interval;
      this.framesReady = true;
    },
    addMarkerWithType(item) {
      if (!this.player) return;
      this.player.pause();

      const time = this.player.currentTime();
      const timeFormatted = this.timeFormatFull(time);
      const selectedItem = { ...item, time_in_video: timeFormatted, time: time }
      
      // Store üzerinden modal'ı aç (selectedItem type a göre sınav modalı eklenecek)
      this.$store.dispatch('openModal', selectedItem);

      this.showMarkerDropdown = false;
    },
    handleEditMarker(idx) {
      const marker = this.markers[idx];
      this.$emit('edit-marker', marker, updated => {
        if (updated) {
          // Store'da marker'ı güncelle
          this.$store.dispatch('updateMarker', { 
            markerId: marker.id, 
            updatedMarker: updated 
          });
        }
      });
    },
    onMarkerDragStart(idx, event) {
      this.measureTimeline();
      this.draggingMarkerIdx = idx;
      this.isDragging = true;
      
      // drag-drop durumlarını tutu, listelmede zamana göre sırlama için vs kullanılacak
      const marker = this.markers[idx];
      this.$store.dispatch('startMarkerDrag', { 
        markerId: marker.id, 
        startTime: marker.time 
      });
      
      document.addEventListener('mousemove', this.onMarkerDragMove);
      document.addEventListener('mouseup', this.onMarkerDragEnd);
      document.addEventListener('touchmove', this.onMarkerDragMove);
      document.addEventListener('touchend', this.onMarkerDragEnd);
    },
    onMarkerDragMove(event) {
      if (this.draggingMarkerIdx === null || !this.player) return;
      const timeline = this.$el.querySelector('.timeline-bar');
      if (!timeline) return;
      const rect = timeline.getBoundingClientRect();
      let clientX = event.touches ? event.touches[0].clientX : event.clientX;
      let percent = (clientX - rect.left) / rect.width;
      percent = Math.max(0, Math.min(percent, 1));
      const newTime = percent * this.player.duration();
      const timeFormatted = this.timeFormatFull(newTime);
      
      // Store'da marker'ı güncelle
      const marker = this.markers[this.draggingMarkerIdx];
      this.$store.dispatch('updateMarker', { 
        markerId: marker.id, 
        updatedMarker: { 
          time: newTime,
          time_in_video: timeFormatted
        } 
      });

      // Görsel güncelleme için force update
      this.$forceUpdate();
    },
    onMarkerDragEnd() {
      if (this.draggingMarkerIdx !== null && this.player) {
        const marker = this.markers[this.draggingMarkerIdx];
        if (marker && typeof marker.time === 'number') {
          // sürükle bırak işlmelerinde time kontolleri için
          const timeFormatted = this.timeFormatFull(marker.time);
          this.$store.dispatch('updateMarker', { 
            markerId: marker.id, 
            updatedMarker: { 
              time: marker.time,
              time_in_video: timeFormatted
            } 
          });

          this.player.currentTime(marker.time);
        }
      }
      
      // Store'da drag state'ini bitir
      this.$store.dispatch('endMarkerDrag');
      
      this.draggingMarkerIdx = null;
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onMarkerDragMove);
      document.removeEventListener('mouseup', this.onMarkerDragEnd);
      document.removeEventListener('touchmove', this.onMarkerDragMove);
      document.removeEventListener('touchend', this.onMarkerDragEnd);
    },
    onTimelineSeek(e) {
      if (!this.player || !this.player.duration()) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = Math.max(0, Math.min((e.clientX - rect.left) / rect.width, 1));
      const seekTime = percent * this.player.duration();
      const clampedTime = Math.max(0, Math.min(seekTime, this.player.duration()));
      this.player.currentTime(clampedTime);
    },
    getTimelineIndex(time) {
      return Math.min(
        Math.floor(time / this.frameInterval),
        this.timelineThumbnails.length - 1
      );
    },
    getClosestThumbnail(time) {
      const idx = this.getTimelineIndex(time);
      return this.timelineThumbnails[idx] || this.placeholderThumb;
    },
    timeFormat(time) {
      let minute = Math.floor(time / 60);
      minute = minute.toString().padStart(2, "0");
      time = Math.floor(time) % 60;
      time = time.toString().padStart(2, "0");
      return minute + ":" + time;
    },
    
    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00';
      
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      
      // Eğer saat varsa HH:MM:SS, yoksa MM:SS formatında göster
      if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      } else {
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
    },
    timeFormatFull(time) {
      const hours = Math.floor(time / 3600);
      const minutes = Math.floor((time % 3600) / 60);
      const seconds = Math.floor(time % 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
    measureTimeline() {
      const timeline = this.$el.querySelector('.timeline-bar');
      if (timeline) {
        this.timelineWidth = timeline.offsetWidth;
      }
    },
    getMarkerStyle(marker) {
      if (!this.player) return {};
      if (!this.timelineWidth) {
        const percent = (marker.time / (this.player.duration() || 1)) * 100;
        return {
          left: percent + '%',
          transform: 'translateX(-50%)',
          transition: this.isDragging ? 'none' : 'left 0.1s',
        };
      }
      const percent = (marker.time / (this.player.duration() || 1));
      let leftPx = percent * this.timelineWidth;
      leftPx = Math.max(this.markerWidth / 2, leftPx);
      leftPx = Math.min(leftPx, this.timelineWidth - this.markerWidth / 2);
      return {
        left: leftPx + 'px',
        transform: 'translateX(-50%)',
        transition: this.isDragging ? 'none' : 'left 0.1s',
      };
    },
    getThumbStyle(marker) {
      if (!this.player) return {};
      if (!this.timelineWidth) {
        const percent = (marker.time / (this.player.duration() || 1)) * 100;
        return {
          left: percent + '%',
          transform: 'translateX(-50%)',
        };
      }
      const percent = (marker.time / (this.player.duration() || 1));
      let leftPx = percent * this.timelineWidth;
      leftPx = Math.max(this.thumbWidth / 2, leftPx);
      leftPx = Math.min(leftPx, this.timelineWidth - this.thumbWidth / 2);
      return {
        left: leftPx + 'px',
        transform: 'translateX(-50%)',
      };
    },
  },
  mounted() {
    // parent component :key ile her defasında refresh edildiğinden , her video değişimnde mount çalışacak
    this.$nextTick(() => {
      this.initializeVideoPlayer();
      this.measureTimeline();
      window.addEventListener('resize', this.measureTimeline);
    });
  },
  beforeDestroy() {
    this.destroyVideoPlayer();
    window.removeEventListener('resize', this.measureTimeline);
  }
};
</script>

<style scoped>
.interactive-video-container {
  width: 100%;
}

.video-responsive {
  height: 500px;
  text-align: center;
  margin-bottom: 1rem;
  position: relative;
}

.video-js {
  width: 100%;
  height: calc(100vh - 250px);
  min-height: 600px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.video-time-display {
  position: absolute;
  top: 0;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  z-index: 999;
  display: flex;
  gap: 10px;
  backdrop-filter: blur(4px);
  min-width: 100px;
  justify-content: center;
  font-family: 'Courier New', monospace;
}

.current-time {
  color: #28a745;
  font-weight: 600;
}

.total-time {
  color: #fcfcfd;
  font-weight: 500;
}

/* Custom Timeline */
.custom-timeline {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 10px auto;
  position: relative;
  z-index: 20;
  padding: 10px 0 0 0;
}

.timeline-bar {
  width: 100%;
  height: 100px;
  background: #eee;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  margin: 0 12px;
}

.timeline-progress {
  height: 100%;
  background: #23ade5;
  border-radius: 6px;
  transition: width 0.1s;
}

.timeline-marker {
  position: absolute;
  top: 70%;
  left: 0;
  width: 24px;
  height: 24px;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 2;
  transition: left 0.1s;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-marker svg {
  display: block;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
  width: 24px;
  height: 24px;
}

.timeline-thumbnails {
  width: 100%;
  height: 72px;
  position: relative;
  margin-top: 4px;
}

.timeline-thumb {
  position: absolute;
  top: 0;
  width: 60px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.timeline-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

/* No image state - show only time */
.timeline-thumb--no-image {
  height: 40px;
  min-height: 40px;
  background: linear-gradient(135deg, #f8fafd 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  justify-content: center;
}

.timeline-thumb--no-image .timeline-thumb-time {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  text-align: center;
  padding: 8px 4px;
  line-height: 1.2;
}

.marker-dropdown-container {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 200;
}

.add-marker-btn {
  background: #23ade5;
  color: #fff;
  border: none;
  border-radius: 24px;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
}

.add-marker-btn .dropdown-arrow {
  margin-left: 8px;
  font-size: 18px;
}

.marker-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 180px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  margin-top: 4px;
  overflow: hidden;
}

.marker-dropdown-item {
  padding: 12px 18px;
  cursor: pointer;
  font-size: 15px;
  color: #222;
  transition: background 0.15s;
}

.marker-dropdown-item:hover {
  background: #f0f4f8;
}

.timeline-images {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  height: 80px;
  margin-bottom: 0;
  z-index: 2;
  pointer-events: none;
  overflow: hidden;
}

.timeline-image-thumb {
  max-width: 120px;
  min-width: 0;
  height: 75px;
  margin: 0 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.timeline-image-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
  box-shadow: 0 1px 8px rgba(0,0,0,0.18);
  background: #fff;
}

.timeline-image-range {
  width: 100%;
  text-align: center;
  font-size: 10px;
  color: #666;
  background: rgba(255,255,255,0.85);
  padding: 2px 0;
  border-radius: 0 0 4px 4px;
  margin-top: 2px;
  pointer-events: none;
}

.timeline-labels {
  width: 100%;
  position: relative;
  height: 24px;
  margin-top: 2px;
}
.timeline-label {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 13px;
  color: #888;
  background: rgba(255,255,255,0.8);
  padding: 0 6px;
  border-radius: 4px;
  pointer-events: none;
}

.timeline-thumb-range-inside {
  width: 100%;
  text-align: center;
  font-size: 12px;
  color: #666;
  background: rgba(255,255,255,0.85);
  padding: 2px 0;
  border-radius: 0 0 4px 4px;
  margin-top: 2px;
  position: absolute;
  left: 0;
  bottom: -22px;
  pointer-events: none;
}

.timeline-thumb-marker {
  position: absolute;
  top: 0;
  left: 0;
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.timeline-thumb-marker img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

/* Marker kucuk resim seridi yalniz desktop'ta. Dar ekranda 60px'lik
   thumb'lar ust uste biniyor ve 76px yer kapliyor; timeline bari zaten
   marker konumlarini gosteriyor. */
@media (max-width: 959px) {
  .timeline-thumbnails {
    display: none;
  }
}
</style>
